import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ProfileOverviewActionsService } from '@app/profile/components/profile-overview/profile-overview-actions/profile-overview-actions.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { InputsService } from '@app/inputs/services/inputs.service';
import { ProfileCollectionItem } from '@app/profile/profile-api.model';
import { InputType } from '@app/shared/models/core-api.model';
import { InputDetails } from '@app/inputs/inputs-api.model';
import { InputModalDispatcherService } from '@app/user-content/user-input/services/input-modal-dispatcher.service';
import { ContentCardService } from '@app/shared/components/content/content-card/content-card.service';
import { Params } from '@angular/router';
import { ReactiveListVM } from '@dg/shared-rsm';
import { LearningItem } from '@app/profile/components/profile-overview/learning-v2/profile-learning.facade';
import { getEmptyTileCount as _getEmptyTileCount } from '@app/profile/components/profile-overview-v2/profile-overview.utils';

@Component({
  selector: 'dgx-profile-learning-v2',
  templateUrl: './profile-learning.component.html',
  styleUrls: ['./profile-learning.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProfileLearningComponentV2
  extends SubscriberBaseDirective
  implements OnDestroy
{
  @Input() public learnings: ReactiveListVM<LearningItem>;
  @Input() public profileName: string;
  @Input() public ownerIsViewing: boolean;
  @Input() public isRestrictedView: boolean; // hides viewAll and add buttons in the skill signals page

  @Input() public learningDisplayLimit: number = 3;
  @Input() public useLoadingTemplate: boolean;
  @Input() public useShowMore: boolean;

  @Input() public useCarousel: boolean = true;

  @Input() public isPageLoading: boolean;
  @Input() public pageType: string;
  @Input() public isMobile: boolean = false;
  @Input() public cardRowLimit: number = 3; // only in use when useCarousel is false

  @Output() public loadMoreLearnings = new EventEmitter<void>();

  public viewAllUrl: string;
  public emptyTiles: number[];

  public viewAllUrlQueryParams: Params;
  public indexesOfExpandedCards$: Observable<number[]>;

  public i18n = this.translateService.instant([
    'dgProfileOverview_AddLearning',
    'Core_Article',
    'Core_Podcast',
    'Core_Video',
    'Core_Book',
    'Core_Course',
    'Core_Event',
    'Core_Assessment',
    'dgProfileOverview_LearningTitle',
    'Core_ShowMore',
  ]);

  public getEmptyTileCount = _getEmptyTileCount;

  public menuConfig: MenuViewModel[] = [
    {
      title: this.i18n.Core_Article,
      defaultAction: () => this.showAddModal('Article'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Podcast,
      defaultAction: () => this.showAddModal('Episode'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Video,
      defaultAction: () => this.showAddModal('Video'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Book,
      defaultAction: () => this.showAddModal('Book'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Course,
      defaultAction: () => this.showAddModal('Course'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Event,
      defaultAction: () => this.showAddModal('Event'),
      preventRefocus: true,
    },
    {
      title: this.i18n.Core_Assessment,
      defaultAction: () => this.showAddModal('Assessment'),
      preventRefocus: true,
    },
  ];

  constructor(
    private translateService: TranslateService,
    private inputsService: InputsService,
    private profileOverviewActionsService: ProfileOverviewActionsService,
    private genericModalService: InputModalDispatcherService,
    private contentCardService: ContentCardService
  ) {
    super();
  }

  public modifyOptionsFn = (content, defaultConfig) => {
    return this.profileOverviewActionsService.modifyInputMenuOptions(
      this.ownerIsViewing,
      false,
      content,
      defaultConfig
    );
  };

  public showAddModal(outcomeType: InputType): void {
    this.genericModalService
      .complete({
        inputType: outcomeType,
      })
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.inputsService.notifyInputModified(outcomeType);
      });
  }

  public ngOnInit(): void {
    const ownerUrl = `/${this.profileName}`;
    this.viewAllUrl = `/profile${ownerUrl}/collection`;
    this.viewAllUrlQueryParams = {
      classifier: 'learning',
    };
    this.indexesOfExpandedCards$ =
      this.contentCardService.getIndexesOfExpandedCards();
  }

  public ngOnDestroy(): void {
    this.contentCardService.setIndexesOfExpandedCards([]);
  }

  public trackByConfigId(index: number, section: InputDetails): number {
    return section.inputId;
  }

  public trackByCollectionId(
    index: number,
    achievement: ProfileCollectionItem<InputDetails>
  ) {
    return achievement.itemId;
  }
}
